/**
 * Console view of the contracts signal registry (guard-v2, 02-design §2 / §7).
 * Every row, field range and fault→profile lock on the 保障配置 drawer is
 * derived from `GUARD_SIGNALS`; this file only adds copy keys and the values a
 * field starts from when an operator turns it on (the DEMO's recommendations).
 */
import {
  GUARD_PROBE_INTERVAL_SECONDS_MAX,
  GUARD_PROBE_INTERVAL_SECONDS_MIN,
  GUARD_PROBE_LONG_MAX_GAP_SECONDS_MAX,
  GUARD_PROBE_LONG_MAX_GAP_SECONDS_MIN,
  GUARD_PROBE_LONG_MIN_STREAM_SECONDS_MAX,
  GUARD_PROBE_LONG_MIN_STREAM_SECONDS_MIN,
  GUARD_PROBE_LONG_TIMEOUT_SECONDS_MAX,
  GUARD_PROBE_LONG_TIMEOUT_SECONDS_MIN,
  GUARD_PROBE_SHORT_TIMEOUT_SECONDS_MAX,
  GUARD_PROBE_SHORT_TIMEOUT_SECONDS_MIN,
  GUARD_PROBE_SHOTS_MAX,
  GUARD_PROBE_SHOTS_MIN,
  GUARD_RECOVER_CLEAN_DEFAULT,
  GUARD_RECOVER_CLEAN_MAX,
  GUARD_RECOVER_CLEAN_MIN,
  GUARD_RETRY_AFTER_CAP_SECONDS_MAX,
  GUARD_RETRY_AFTER_CAP_SECONDS_MIN,
  GUARD_LONG_STREAM_PROFILE_DEFAULT,
  GUARD_SIGNALS,
  GUARD_TRIP_MAX_HOLD_SECONDS_MAX,
  GUARD_TRIP_MAX_HOLD_SECONDS_MIN,
  GUARD_TRIP_MIN_HITS_MAX,
  GUARD_TRIP_MIN_HITS_MIN,
  GUARD_TRIP_WINDOW_SECONDS_MAX,
  GUARD_TRIP_WINDOW_SECONDS_MIN,
  type GuardLongStreamProfileSpec,
  type GuardProbeProfile,
  type GuardShortProfileSpec,
  type GuardSignalKind,
  type GuardSignalSpec,
} from '@nadoutong/contracts';
import type { I18nKey } from '../../../i18n/index.ts';

export interface GuardRange {
  readonly min: number;
  readonly max: number;
}

export interface GuardTripDefaults {
  readonly window_s: number;
  readonly min_hits: number;
  /** Percent (0–100). */
  readonly ratio_pct: number;
  readonly max_hold_s: number;
}

export interface GuardSignalView {
  readonly spec: GuardSignalSpec;
  readonly title: I18nKey;
  readonly short: I18nKey;
  readonly desc: I18nKey;
  readonly tag: I18nKey | null;
  readonly judge: I18nKey | null;
  readonly thresholdLabel: I18nKey | null;
  readonly walkTitle: I18nKey | null;
  readonly walkNote: I18nKey | null;
  readonly tripTitle: I18nKey;
  readonly tripNote: I18nKey | null;
  /** Threshold the field starts from when turned on, in seconds. */
  readonly defaultThresholdS: number | null;
  readonly tripDefaults: GuardTripDefaults;
  readonly defaultClean: number;
  /** D2: stall keeps the short probe after migration; long stream is the page's recommendation. */
  readonly recommendLong: boolean;
}

const VIEW_COPY: Readonly<Record<GuardSignalKind, Omit<GuardSignalView, 'spec'>>> = {
  overload: {
    title: 'guaranteed.guard.signal.overload.title',
    short: 'guaranteed.guard.signal.overload.short',
    desc: 'guaranteed.guard.signal.overload.desc',
    tag: null,
    judge: 'guaranteed.guard.signal.overload.judge',
    thresholdLabel: null,
    walkTitle: 'guaranteed.guard.signal.overload.walk_title',
    walkNote: 'guaranteed.guard.signal.overload.walk_note',
    tripTitle: 'guaranteed.guard.signal.overload.trip_title',
    tripNote: 'guaranteed.guard.signal.overload.trip_note',
    defaultThresholdS: null,
    tripDefaults: { window_s: 300, min_hits: 5, ratio_pct: 30, max_hold_s: 1200 },
    defaultClean: GUARD_RECOVER_CLEAN_DEFAULT,
    recommendLong: false,
  },
  first_content_stream: {
    title: 'guaranteed.guard.signal.first_content_stream.title',
    short: 'guaranteed.guard.signal.first_content_stream.short',
    desc: 'guaranteed.guard.signal.first_content_stream.desc',
    tag: 'guaranteed.guard.signal.first_content_stream.tag',
    judge: null,
    thresholdLabel: 'guaranteed.guard.signal.first_content_stream.threshold',
    walkTitle: 'guaranteed.guard.signal.first_content_stream.walk_title',
    walkNote: 'guaranteed.guard.signal.first_content_stream.walk_note',
    tripTitle: 'guaranteed.guard.signal.first_content_stream.trip_title',
    tripNote: null,
    defaultThresholdS: 45,
    tripDefaults: { window_s: 300, min_hits: 3, ratio_pct: 10, max_hold_s: 1800 },
    defaultClean: GUARD_RECOVER_CLEAN_DEFAULT,
    recommendLong: false,
  },
  first_content_nonstream: {
    title: 'guaranteed.guard.signal.first_content_nonstream.title',
    short: 'guaranteed.guard.signal.first_content_nonstream.short',
    desc: 'guaranteed.guard.signal.first_content_nonstream.desc',
    tag: 'guaranteed.guard.signal.first_content_nonstream.tag',
    judge: null,
    thresholdLabel: 'guaranteed.guard.signal.first_content_nonstream.threshold',
    walkTitle: 'guaranteed.guard.signal.first_content_nonstream.walk_title',
    walkNote: 'guaranteed.guard.signal.first_content_nonstream.walk_note',
    tripTitle: 'guaranteed.guard.signal.first_content_nonstream.trip_title',
    tripNote: null,
    defaultThresholdS: 45,
    tripDefaults: { window_s: 300, min_hits: 3, ratio_pct: 10, max_hold_s: 1800 },
    defaultClean: GUARD_RECOVER_CLEAN_DEFAULT,
    recommendLong: false,
  },
  prelude_fail: {
    title: 'guaranteed.guard.signal.prelude_fail.title',
    short: 'guaranteed.guard.signal.prelude_fail.short',
    desc: 'guaranteed.guard.signal.prelude_fail.desc',
    tag: 'guaranteed.guard.signal.prelude_fail.tag',
    judge: 'guaranteed.guard.signal.prelude_fail.judge',
    thresholdLabel: null,
    walkTitle: 'guaranteed.guard.signal.prelude_fail.walk_title',
    walkNote: 'guaranteed.guard.signal.prelude_fail.walk_note',
    tripTitle: 'guaranteed.guard.signal.prelude_fail.trip_title',
    tripNote: null,
    defaultThresholdS: null,
    tripDefaults: { window_s: 300, min_hits: 3, ratio_pct: 10, max_hold_s: 1800 },
    defaultClean: GUARD_RECOVER_CLEAN_DEFAULT,
    recommendLong: false,
  },
  stall: {
    title: 'guaranteed.guard.signal.stall.title',
    short: 'guaranteed.guard.signal.stall.short',
    desc: 'guaranteed.guard.signal.stall.desc',
    tag: null,
    judge: 'guaranteed.guard.signal.stall.judge',
    thresholdLabel: 'guaranteed.guard.signal.stall.threshold',
    walkTitle: 'guaranteed.guard.signal.stall.walk_title',
    walkNote: 'guaranteed.guard.signal.stall.walk_note',
    tripTitle: 'guaranteed.guard.signal.stall.trip_title',
    tripNote: null,
    defaultThresholdS: 150,
    tripDefaults: { window_s: 300, min_hits: 3, ratio_pct: 5, max_hold_s: 1800 },
    defaultClean: GUARD_RECOVER_CLEAN_DEFAULT,
    recommendLong: true,
  },
  broken: {
    title: 'guaranteed.guard.signal.broken.title',
    short: 'guaranteed.guard.signal.broken.short',
    desc: 'guaranteed.guard.signal.broken.desc',
    tag: null,
    judge: 'guaranteed.guard.signal.broken.judge',
    thresholdLabel: null,
    walkTitle: null,
    walkNote: null,
    tripTitle: 'guaranteed.guard.signal.broken.trip_title',
    tripNote: null,
    defaultThresholdS: null,
    tripDefaults: { window_s: 300, min_hits: 3, ratio_pct: 50, max_hold_s: 3600 },
    defaultClean: 3,
    recommendLong: false,
  },
};

export const GUARD_SIGNAL_VIEWS: readonly GuardSignalView[] = GUARD_SIGNALS.map((spec) => ({
  spec,
  ...VIEW_COPY[spec.kind],
}));

export function signalView(kind: GuardSignalKind): GuardSignalView {
  const view = GUARD_SIGNAL_VIEWS.find((entry) => entry.spec.kind === kind);
  if (view === undefined) throw new Error(`no console view for guard signal ${kind}`);
  return view;
}

export const TRIP_RANGES = {
  window_s: { min: GUARD_TRIP_WINDOW_SECONDS_MIN, max: GUARD_TRIP_WINDOW_SECONDS_MAX },
  min_hits: { min: GUARD_TRIP_MIN_HITS_MIN, max: GUARD_TRIP_MIN_HITS_MAX },
  ratio_pct: { min: 0, max: 100 },
  max_hold_s: { min: GUARD_TRIP_MAX_HOLD_SECONDS_MIN, max: GUARD_TRIP_MAX_HOLD_SECONDS_MAX },
} as const satisfies Readonly<Record<keyof GuardTripDefaults, GuardRange>>;

export const CLEAN_RANGE: GuardRange = { min: GUARD_RECOVER_CLEAN_MIN, max: GUARD_RECOVER_CLEAN_MAX };
export const RETRY_AFTER_CAP_RANGE: GuardRange = {
  min: GUARD_RETRY_AFTER_CAP_SECONDS_MIN,
  max: GUARD_RETRY_AFTER_CAP_SECONDS_MAX,
};
export const RETRY_AFTER_CAP_DEFAULT_S = 30;

export function thresholdRangeSeconds(spec: GuardSignalSpec): GuardRange | null {
  if (spec.threshold === null) return null;
  return { min: spec.threshold.min / 1000, max: spec.threshold.max / 1000 };
}

export const PROFILE_DEFAULTS: {
  readonly short: GuardShortProfileSpec;
  readonly long_stream: GuardLongStreamProfileSpec;
} = {
  short: { interval_s: 300, shots: 2, timeout_s: 120 },
  // R1-B6: reachable with the probe's 8192-token budget (contracts).
  long_stream: { ...GUARD_LONG_STREAM_PROFILE_DEFAULT },
};

export const PROFILE_RANGES = {
  interval_s: { min: GUARD_PROBE_INTERVAL_SECONDS_MIN, max: GUARD_PROBE_INTERVAL_SECONDS_MAX },
  shots: { min: GUARD_PROBE_SHOTS_MIN, max: GUARD_PROBE_SHOTS_MAX },
  short_timeout_s: { min: GUARD_PROBE_SHORT_TIMEOUT_SECONDS_MIN, max: GUARD_PROBE_SHORT_TIMEOUT_SECONDS_MAX },
  long_timeout_s: { min: GUARD_PROBE_LONG_TIMEOUT_SECONDS_MIN, max: GUARD_PROBE_LONG_TIMEOUT_SECONDS_MAX },
  min_stream_s: { min: GUARD_PROBE_LONG_MIN_STREAM_SECONDS_MIN, max: GUARD_PROBE_LONG_MIN_STREAM_SECONDS_MAX },
  max_gap_s: { min: GUARD_PROBE_LONG_MAX_GAP_SECONDS_MIN, max: GUARD_PROBE_LONG_MAX_GAP_SECONDS_MAX },
} as const satisfies Readonly<Record<string, GuardRange>>;

export function profileLabel(profile: GuardProbeProfile): I18nKey {
  return profile === 'long_stream' ? 'guaranteed.guard.recover.profile.long_stream' : 'guaranteed.guard.recover.profile.short';
}
